import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import {
  Button,
  Drawer,
  message,
  Badge,
  Tooltip,
  Card,
  Space,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useList } from '@refinedev/core';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CreateForm, EditForm } from '../shared/forms';
import { SERVICE_TYPE_OPTIONS } from '@src/pages/appointments/constants';

const { Title, Text } = Typography;

interface CalendarEvent {
  id: number;
  [key: string]: any;
}

interface CalendarComponentProps {
  // API Configuration
  resource: string;

  // Event Configuration
  eventFields: {
    dateField: string;
    nameField: string;
    statusField?: string;
    serviceField?: string;
  };

  // Form Configuration
  createForm?: {
    fields: any[];
    title?: string;
    submitText?: string;
    initialValues?: Record<string, any>;
  };
  editForm?: {
    fields: any[];
    title?: string;
    submitText?: string;
    customTitle?: React.ReactNode | ((selectedEvent: any) => React.ReactNode);
  };

  // Status Configuration
  statusConfig?: {
    colors: Record<string, string>;
    bgColors: Record<string, string>;
    legend?: Array<{
      status: string;
      label: string;
      color: string;
    }>;
  };

  // Calendar Configuration
  calendarConfig?: {
    showStatusLegend?: boolean;
    showCreateButton?: boolean;
    createButtonText?: string;
    dateFormat?: string;
    timeFormat?: string;
    showViewSelector?: boolean;
    defaultView?: 'month' | 'week' | 'day' | 'agenda';
  };

  // Callbacks
  onEventClick?: (event: CalendarEvent) => void;
  onEditClick?: (record: any) => void;
  onCreateSuccess?: (data: any) => void;
  onEditSuccess?: (data: any) => void;
  onCreateDrawerClose?: () => void;

  // Custom renderers
  renderEventContent?: (event: CalendarEvent) => React.ReactNode;
  renderHeader?: (props: {
    value: Dayjs;
    onChange: (date: Dayjs) => void;
  }) => React.ReactNode;

  // View tabs for switching between views
  viewTabs?: React.ReactNode;

  // Chat configuration
  enableChat?: boolean;
  chatType?: string;
  chatRelativeId?: number;
  chatPatientId?: number;
  chatTitle?: string;
  chatPlaceholder?: string;

  // Form display configuration
  formDisplayMode?: 'drawer' | 'modal' | 'inline';
  drawerWidth?: number;
  modalWidth?: number;
  showCreate?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;

  // Auto-open create configuration
  autoOpenCreate?: boolean;

  // Additional configuration
  pageSize?: number;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showExport?: boolean;
  showColumnVisibility?: boolean;
  exportFormats?: string[];
  scroll?: any;
  rowKey?: string;
  style?: React.CSSProperties;
  className?: string;
  cardProps?: any;
  tableProps?: any;
  isintialScroll?: boolean;

  /** When set, the visible date and view mode are restored from localStorage after leaving and returning to the page. */
  calendarStateStorageKey?: string;
}

function computeCalendarDateRange(
  date: Dayjs,
  view: 'month' | 'week' | 'day' | 'agenda'
): { start: string; end: string } {
  switch (view) {
    case 'week':
      return {
        start: date.startOf('week').format('YYYY-MM-DD'),
        end: date.endOf('week').format('YYYY-MM-DD'),
      };
    case 'day':
      return {
        start: date.format('YYYY-MM-DD'),
        end: date.format('YYYY-MM-DD'),
      };
    case 'agenda':
      return {
        start: date.startOf('month').format('YYYY-MM-DD'),
        end: date.endOf('month').add(1, 'month').format('YYYY-MM-DD'),
      };
    case 'month':
    default:
      return {
        start: date.startOf('month').format('YYYY-MM-DD'),
        end: date.endOf('month').format('YYYY-MM-DD'),
      };
  }
}

function readPersistedCalendarState(
  key: string | undefined
): { date: Dayjs; view: 'month' | 'week' | 'day' | 'agenda' } | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      selectedDate?: string;
      view?: string;
    };
    const d = parsed.selectedDate ? dayjs(parsed.selectedDate) : null;
    if (!d?.isValid()) return null;
    const v = parsed.view;
    if (v !== 'month' && v !== 'week' && v !== 'day' && v !== 'agenda') {
      return null;
    }
    return { date: d, view: v };
  } catch {
    return null;
  }
}

const defaultStatusConfig = {
  colors: {
    CONFIRMED: '#52c41a',
    PENDING: '#faad14',
    CANCELLED: '#ff4d4f',
    COMPLETED: '#1890ff',
  },
  bgColors: {
    CONFIRMED: '#f6ffed',
    PENDING: '#fffbe6',
    CANCELLED: '#fff2f0',
    COMPLETED: '#f0f9ff',
  },
  legend: [
    { status: 'CONFIRMED', label: 'Confirmed', color: '#52c41a' },
    { status: 'PENDING', label: 'Pending', color: '#faad14' },
    { status: 'CANCELLED', label: 'Cancelled', color: '#ff4d4f' },
    { status: 'COMPLETED', label: 'Completed', color: '#1890ff' },
  ],
};

// Setup localizer
const localizer = momentLocalizer(moment);

export const CalendarComponent: React.FC<CalendarComponentProps> = ({
  resource,
  eventFields,
  createForm,
  editForm,
  statusConfig = defaultStatusConfig,
  calendarConfig = {},
  onEventClick,
  onEditClick,
  onCreateSuccess,
  onEditSuccess,
  onCreateDrawerClose,
  renderEventContent,
  renderHeader,
  viewTabs,
  enableChat,
  chatType,
  chatRelativeId,
  chatPatientId,
  chatTitle,
  chatPlaceholder,
  formDisplayMode = 'drawer',
  drawerWidth = 720,
  modalWidth = 720,
  showCreate = true,
  showEdit = true,
  showDelete = true,
  showView = true,
  autoOpenCreate = false,
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = true,
  showExport = true,
  showColumnVisibility = true,
  exportFormats,
  scroll,
  rowKey = 'id',
  style,
  className,
  cardProps,
  tableProps,
  isintialScroll,
  calendarStateStorageKey,
}) => {
  const {
    showStatusLegend = true,
    showCreateButton = true,
    createButtonText = 'Create Event',
    dateFormat = 'YYYY-MM-DDTHH:mm:ss',
    timeFormat = 'HH:mm',
    showViewSelector = true,
    defaultView = 'day',
  } = calendarConfig;

  const persistedCalendar = readPersistedCalendarState(calendarStateStorageKey);
  const initialCalendarDate = persistedCalendar?.date ?? dayjs();
  const initialCalendarView = persistedCalendar?.view ?? defaultView;

  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    () => initialCalendarDate
  );
  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [createInitialValues, setCreateInitialValues] = useState<any>({});
  const [currentView, setCurrentView] = useState<
    'month' | 'week' | 'day' | 'agenda'
  >(() => initialCalendarView);
  const [isViewChanging, setIsViewChanging] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
    () => computeCalendarDateRange(initialCalendarDate, initialCalendarView)
  );

  useEffect(() => {
    if (!calendarStateStorageKey) return;
    try {
      localStorage.setItem(
        calendarStateStorageKey,
        JSON.stringify({
          selectedDate: selectedDate.toISOString(),
          view: currentView,
        })
      );
    } catch {
      // ignore storage errors
    }
  }, [calendarStateStorageKey, selectedDate, currentView]);

  // Auto-open create drawer when autoOpenCreate is true
  useEffect(() => {
    if (autoOpenCreate && createForm) {
      setIsCreateDrawerVisible(true);
      // Set initial values from createForm if provided
      if (createForm.initialValues) {
        setCreateInitialValues(createForm.initialValues);
      }
    }
  }, [autoOpenCreate, createForm]);

  const calculateDateRange = useCallback(
    (date: Dayjs, view: 'month' | 'week' | 'day' | 'agenda') =>
      computeCalendarDateRange(date, view),
    []
  );

  // Fetch events data with date range filter
  const { data, isLoading, refetch } = useList<CalendarEvent>({
    resource,
    filters: [
      {
        field: eventFields.dateField,
        operator: 'gte',
        value: dateRange.start,
      },
      {
        field: eventFields.dateField,
        operator: 'lte',
        value: dateRange.end,
      },
    ],
    pagination: {
      current: 1,
      pageSize: 1000, // Get all events for calendar
    },
    queryOptions: {
      enabled: true,
    },
  });

  const events = data?.data || [];

  // Refetch data when date range changes
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      refetch();
    }
  }, [dateRange, refetch]);

  // Transform events for react-big-calendar
  const calendarEvents = useMemo(() => {
    return events.map(event => ({
      id: event.id,
      title: event[eventFields.nameField],
      start: moment(event[eventFields.dateField]).toDate(),
      end: moment(event[eventFields.dateField]).add(1, 'hour').toDate(), // Default 1 hour duration
      status: eventFields.statusField ? event[eventFields.statusField] : '',
      service: eventFields.serviceField ? event[eventFields.serviceField] : '',
      resource: event,
    }));
  }, [events, eventFields]);

  // Get status color
  const getStatusColor = useCallback(
    (status: string): string => {
      return (
        (statusConfig.colors as Record<string, string>)[
          status?.toUpperCase()
        ] || '#8c8c8c'
      );
    },
    [statusConfig.colors]
  );

  // Get service type label
  const getServiceTypeLabel = useCallback((serviceType: string): string => {
    const serviceOption = SERVICE_TYPE_OPTIONS.find(
      option => option.value === serviceType
    );
    return serviceOption ? serviceOption.label : serviceType;
  }, []);

  // Handle event click
  const handleEventClick = useCallback(
    (event: any) => {
      if (onEventClick) {
        onEventClick(event.resource);
      } else {
        setSelectedEvent(event.resource);
        setIsEditDrawerVisible(true);
      }
      // Call onEditClick if provided
      if (onEditClick) {
        onEditClick(event.resource);
      }
    },
    [onEventClick, onEditClick]
  );

  // Handle slot selection (for creating new events)
  const handleSlotSelect = useCallback(
    (slotInfo: any) => {
      setCreateInitialValues({
        [eventFields.dateField]: moment(slotInfo.start).format(dateFormat),
      });
      setIsCreateDrawerVisible(true);
    },
    [eventFields.dateField, dateFormat]
  );

  // Handle create form success
  const handleCreateSuccess = useCallback(() => {
    setIsCreateDrawerVisible(false);
    setCreateInitialValues({});
    refetch();
    message.success('Event created successfully');
    onCreateSuccess?.({});
  }, [refetch, onCreateSuccess]);

  // Handle create drawer close
  const handleCreateDrawerClose = useCallback(() => {
    setIsCreateDrawerVisible(false);
    setCreateInitialValues({});
    // Reset any form state if needed
    onCreateDrawerClose?.();
  }, [onCreateDrawerClose]);

  // Handle edit form success
  const handleEditSuccess = useCallback(() => {
    setIsEditDrawerVisible(false);
    setSelectedEvent(null);
    refetch();
    message.success('Event updated successfully');
    onEditSuccess?.({});
  }, [refetch, onEditSuccess]);

  // Handle delete success
  const handleDeleteSuccess = useCallback(() => {
    setIsEditDrawerVisible(false);
    setSelectedEvent(null);
    refetch();
  }, [refetch]);

  // Custom event component
  const EventComponent = useCallback(
    ({ event }: { event: any }) => {
      const statusColor = getStatusColor(event.status);
      const serviceLabel = event.service
        ? getServiceTypeLabel(event.service)
        : '';

      return (
        <Tooltip
          title={`${event.title}${serviceLabel ? ` - ${serviceLabel}` : ''}${event.status ? ` (${event.status})` : ''}`}
          placement="top"
        >
          <div
            className="event-item bg-white border rounded-md p-1 text-xs font-medium cursor-pointer h-full flex flex-col justify-center shadow-sm hover:shadow-md transition-shadow duration-200"
            style={{
              backgroundColor: statusColor,
              color: 'white',
              borderColor: statusColor,
            }}
          >
            <div className="event-title font-semibold mb-0.5">
              {event.title}
            </div>
            {serviceLabel && (
              <div className="event-service text-xs opacity-90">
                {serviceLabel}
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
    [getStatusColor, getServiceTypeLabel]
  );

  // Handle view change
  const handleViewChange = useCallback(
    (view: 'month' | 'week' | 'day' | 'agenda') => {
      setIsViewChanging(true);
      setCurrentView(view);
      const newDateRange = calculateDateRange(selectedDate, view);
      setDateRange(newDateRange);
      // Refetch data immediately when view changes
      setTimeout(() => {
        refetch();
        setIsViewChanging(false);
      }, 0);
    },
    [selectedDate, calculateDateRange, refetch]
  );

  // Sync currentView with calendar's actual view
  useEffect(() => {
    // This ensures our state stays in sync with the calendar's internal state
  }, [currentView]);

  // Handle date navigation
  const handleDateChange = useCallback(
    (date: Date) => {
      const newDate = dayjs(date);
      setSelectedDate(newDate);
      const newDateRange = calculateDateRange(newDate, currentView);
      setDateRange(newDateRange);
      // Refetch data immediately when date changes
      setTimeout(() => refetch(), 0);
    },
    [currentView, calculateDateRange, refetch]
  );

  // Custom toolbar component
  const CustomToolbar = useCallback(
    (toolbar: any) => {
      const goToToday = () => {
        const today = new Date();
        handleDateChange(today);
        toolbar.onNavigate('TODAY');
      };

      const goToPrev = () => {
        let newDate: Dayjs;
        switch (toolbar.view) {
          case 'month':
            newDate = selectedDate.subtract(1, 'month');
            break;
          case 'week':
            newDate = selectedDate.subtract(1, 'week');
            break;
          case 'day':
            newDate = selectedDate.subtract(1, 'day');
            break;
          default:
            newDate = selectedDate.subtract(1, 'month');
        }
        handleDateChange(newDate.toDate());
        toolbar.onNavigate('PREV');
      };

      const goToNext = () => {
        let newDate: Dayjs;
        switch (toolbar.view) {
          case 'month':
            newDate = selectedDate.add(1, 'month');
            break;
          case 'week':
            newDate = selectedDate.add(1, 'week');
            break;
          case 'day':
            newDate = selectedDate.add(1, 'day');
            break;
          default:
            newDate = selectedDate.add(1, 'month');
        }
        handleDateChange(newDate.toDate());
        toolbar.onNavigate('NEXT');
      };

      const handleViewChange = (view: 'month' | 'week' | 'day' | 'agenda') => {
        setCurrentView(view);
        const newDateRange = calculateDateRange(selectedDate, view);
        setDateRange(newDateRange);
        toolbar.onView(view);
        // Refetch data immediately when view changes from toolbar
        setTimeout(() => refetch(), 0);
      };

      const viewNames = {
        month: 'Month',
        week: 'Week',
        day: 'Day',
        agenda: 'Agenda',
      };

      return (
        <div className="rbc-toolbar bg-white border-b border-gray-200 shadow-sm">
          {/* Top row with view tabs and create button */}
          <div className="w-full p-4 pb-2">
            <Row justify="end" align="middle">
              <Col>
                <Space>
                  {viewTabs && viewTabs}
                  {showCreateButton && showCreate && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setCreateInitialValues({
                          [eventFields.dateField]: dayjs().format(dateFormat),
                        });
                        setIsCreateDrawerVisible(true);
                      }}
                      style={{
                        backgroundColor: '#1677ff',
                        borderColor: '#1677ff',
                        color: '#fff',
                      }}
                    >
                      <span className="ml-2">Create</span>
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {/* Bottom row with calendar navigation */}
          <div className="w-full px-4 pb-4 pt-4">
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Button
                    type="default"
                    icon={<CalendarOutlined />}
                    onClick={goToToday}
                    className="rounded-lg today-button"
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderColor: '#d9d9d9',
                      color: '#262626',
                    }}
                  >
                    Today
                  </Button>
                  <Button.Group>
                    <Button
                      onClick={goToPrev}
                      className="rounded-l-lg nav-button"
                      style={{
                        backgroundColor: '#fff',
                        borderColor: '#d9d9d9',
                        color: '#262626',
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      onClick={goToNext}
                      className="rounded-r-lg nav-button"
                      style={{
                        backgroundColor: '#fff',
                        borderColor: '#d9d9d9',
                        color: '#262626',
                      }}
                    >
                      Next
                    </Button>
                  </Button.Group>
                </Space>
              </Col>

              <Col>
                <div className="rbc-toolbar-label text-lg font-semibold text-gray-800">
                  {toolbar.label}
                </div>
              </Col>

              <Col>
                <Button.Group>
                  <Button
                    type={currentView === 'month' ? 'primary' : 'default'}
                    style={{
                      backgroundColor:
                        currentView === 'month' ? '#1677ff' : '#fff',
                      borderColor: '#d9d9d9',
                      color: currentView === 'month' ? '#fff' : '#262626',
                    }}
                    onClick={() => {
                      handleViewChange('month');
                      toolbar.onView('month');
                    }}
                    className="rounded-l-lg"
                  >
                    Month
                  </Button>
                  <Button
                    type={currentView === 'week' ? 'primary' : 'default'}
                    style={{
                      backgroundColor:
                        currentView === 'week' ? '#1677ff' : '#fff',
                      borderColor: '#d9d9d9',
                      color: currentView === 'week' ? '#fff' : '#262626',
                    }}
                    onClick={() => {
                      handleViewChange('week');
                      toolbar.onView('week');
                    }}
                  >
                    Week
                  </Button>
                  <Button
                    type={currentView === 'day' ? 'primary' : 'default'}
                    style={{
                      backgroundColor:
                        currentView === 'day' ? '#1677ff' : '#fff',
                      borderColor: '#d9d9d9',
                      color: currentView === 'day' ? '#fff' : '#262626',
                    }}
                    onClick={() => {
                      handleViewChange('day');
                      toolbar.onView('day');
                    }}
                  >
                    Day
                  </Button>
                  <Button
                    type={currentView === 'agenda' ? 'primary' : 'default'}
                    style={{
                      backgroundColor:
                        currentView === 'agenda' ? '#1677ff' : '#fff',
                      borderColor: '#d9d9d9',
                      color: currentView === 'agenda' ? '#fff' : '#262626',
                    }}
                    onClick={() => {
                      handleViewChange('agenda');
                      toolbar.onView('agenda');
                    }}
                    className="rounded-r-lg"
                  >
                    Agenda
                  </Button>
                </Button.Group>
              </Col>
            </Row>
          </div>
        </div>
      );
    },
    [
      showViewSelector,
      showCreateButton,
      showCreate,
      createButtonText,
      eventFields.dateField,
      dateFormat,
      viewTabs,
      selectedDate,
      handleDateChange,
      calculateDateRange,
    ]
  );

  // Status legend component
  const StatusLegend = useCallback(() => {
    if (!showStatusLegend || !statusConfig.legend) return null;

    return (
      <Card size="small" className="mb-4 rounded-lg shadow-md">
        <div className="flex items-center gap-4 flex-wrap">
          <Text strong className="text-gray-800">
            Status Legend:
          </Text>
          {statusConfig.legend.map(item => (
            <Badge
              key={item.status}
              color={item.color}
              text={item.label}
              className="text-xs"
            />
          ))}
        </div>
      </Card>
    );
  }, [showStatusLegend, statusConfig.legend]);

  return (
    <div
      className={`calendar-container h-full flex flex-col ${className || ''}`}
    >
      {/* Calendar */}
      <div className="calendar-wrapper flex-1 bg-white rounded-lg overflow-hidden shadow-lg">
        {isLoading || isViewChanging ? (
          <div className="flex justify-center items-center h-full text-base text-gray-600">
            {isViewChanging ? 'Changing view...' : 'Loading events...'}
          </div>
        ) : (
          <Calendar
            key={`calendar-${currentView}-${selectedDate.format('YYYY-MM-DD')}`}
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            className="h-full custom-calendar"
            views={['month', 'week', 'day', 'agenda']}
            view={currentView}
            date={selectedDate.toDate()}
            onNavigate={handleDateChange}
            onView={view => {
              if (
                view === 'month' ||
                view === 'week' ||
                view === 'day' ||
                view === 'agenda'
              ) {
                handleViewChange(view);
              }
            }}
            onSelectEvent={handleEventClick}
            onSelectSlot={handleSlotSelect}
            selectable
            components={{
              toolbar: CustomToolbar,
              event: EventComponent,
            }}
            eventPropGetter={event => ({
              style: {
                backgroundColor: getStatusColor(event.status),
                borderColor: getStatusColor(event.status),
              },
            })}
          />
        )}
      </div>

      {/* Create Form Drawer */}
      {createForm && showCreate && (
        <CreateForm
          key={`calendar-create-form-${isCreateDrawerVisible}`}
          resource={resource}
          fields={createForm.fields}
          title={createForm.title || 'Create Event'}
          submitText={createForm.submitText || 'Create'}
          initialValues={createInitialValues}
          open={isCreateDrawerVisible}
          onClose={handleCreateDrawerClose}
          onSuccess={handleCreateSuccess}
          formDisplayMode={formDisplayMode}
          drawerWidth={drawerWidth}
          modalWidth={modalWidth}
          enableChat={enableChat}
          chatType={chatType}
          chatRelativeId={chatRelativeId}
          chatTitle={chatTitle}
          chatPlaceholder={chatPlaceholder}
        />
      )}

      {/* Edit Form Drawer */}
      {editForm && selectedEvent && showEdit && (
        <EditForm
          resource={resource}
          fields={editForm.fields}
          title={editForm.title || 'Edit Event'}
          submitText={editForm.submitText || 'Save Changes'}
          initialValues={selectedEvent}
          open={isEditDrawerVisible}
          onClose={() => {
            setIsEditDrawerVisible(false);
            setSelectedEvent(null);
          }}
          onSuccess={handleEditSuccess}
          formDisplayMode={formDisplayMode}
          drawerWidth={drawerWidth}
          modalWidth={modalWidth}
          enableChat={enableChat}
          chatType={chatType}
          chatRelativeId={chatRelativeId || selectedEvent?.id}
          chatPatientId={chatPatientId}
          chatTitle={chatTitle}
          chatPlaceholder={chatPlaceholder}
          isintialScroll={isintialScroll}
          showDelete={showDelete}
          onDeleteSuccess={handleDeleteSuccess}
          customTitle={
            typeof editForm.customTitle === 'function'
              ? editForm.customTitle(selectedEvent)
              : editForm.customTitle
          }
        />
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .custom-calendar {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          }
          
          .custom-calendar .rbc-header {
            background-color: #fafafa;
            border-bottom: 1px solid #f0f0f0;
            padding: 12px 8px;
            font-weight: 600;
            color: #262626;
          }
          
          .custom-calendar .rbc-month-view {
            border-radius: 8px;
          }
          
          .custom-calendar .rbc-day-bg {
            border-color: #f0f0f0;
          }
          
          .custom-calendar .rbc-today {
            background-color: #e6f7ff;
          }
          
          .custom-calendar .rbc-off-range-bg {
            background-color: #fafafa;
          }
          
          .custom-calendar .rbc-off-range {
            color: #bfbfbf;
          }
          
          .custom-calendar .rbc-event {
            
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .custom-calendar .event-item {
            padding: 0px !important;
            box-shadow: none !important;
          }

          .custom-calendar .event-title {
            font-size: 10px !important;
            margin: 0px !important;
          }
          .custom-calendar .event-service {
            font-size: 10px !important;
          }
          
          .custom-calendar .rbc-time-view {
            border-radius: 8px;
          }
          
          .custom-calendar .rbc-time-header {
            border-radius: 8px 8px 0 0;
          }
          
          .custom-calendar .rbc-agenda-view {
            border-radius: 8px;
          }
          
          .custom-calendar .rbc-agenda-view table.rbc-agenda-table {
            border-radius: 8px;
          }
          
          .custom-calendar .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
            padding: 12px 16px;
            border-color: #f0f0f0;
          }
          
          .custom-calendar .rbc-agenda-view table.rbc-agenda-table tbody > tr:hover > td {
            background-color: #f5f5f5;
          }
          
          .today-button:hover {
            background-color: #1677ff !important;
            border-color: #1677ff !important;
            color: #fff !important;
          }
          
          .nav-button:hover {
            background-color: #1677ff !important;
            border-color: #1677ff !important;
            color: #fff !important;
          }


          // week view styles
          .custom-calendar .rbc-time-header-content .rbc-header {
            padding: 0px !important;
        }

            .custom-calendar .rbc-time-header-content .rbc-header .rbc-button-link {
            padding: 4px !important;
        }

        .custom-calendar .rbc-time-content .rbc-day-slot .rbc-events-container .rbc-event-label {
            font-size: 8px !important;
        }

        .custom-calendar .rbc-time-content .rbc-day-slot .rbc-events-container .event-item {
            font-size: 9px !important;
            line-height: 9px !important;
            background-color: none !important;
        }

        .custom-calendar .rbc-time-content .rbc-day-slot .rbc-events-container .event-title , .custom-calendar .rbc-time-content .rbc-day-slot .rbc-events-container .event-service {
            font-size: 9px !important;
            height: 10px !important;    
        }

        .rbc-time-content .rbc-day-slot .rbc-events-container {
          margin-right: 0px !important;
        }


          //  agenda view styles
          // .rbc-agenda-view .rbc-agenda-content .rbc-agenda-table tbody > tr {
          //   background-color: #f0f0f0 !important;
          //   border: none !important;
          // }

          .rbc-agenda-view .rbc-agenda-content .rbc-agenda-table tbody > tr {
        background-color: #f0f0f0 !important;
        border-color: transparent !important;
      }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
            border-right: 1px solid #ddd !important;
          }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
            border-bottom: 1px solid #ddd !important;
          }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td {
            border-bottom: 1px solid #ddd !important;
          }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td .event-item {
            padding: 4px !important;
          }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td + td .event-item {
            padding: 4px !important;
          }



        

      
        `,
        }}
      />
    </div>
  );
};
